/* eslint-disable import/no-default-export */
import classNames from 'classnames';
import React from 'react';

import RouterLink from 'app/common/components/singlespa/router-link';

import { Icon, IconName } from 'app/common/components/controls';

import { Noun, Verb, trackUe } from '@trello/analytics';
import styles from './collapsible.less';

interface AvatarProps {
  readonly avatarURL?: string;
  readonly icon?: IconName;
}

export interface CollapsibleProps extends AvatarProps {
  readonly id: string;
  readonly categoryId: string;
  readonly title: string;
  readonly open: boolean;
  readonly headerClass: string;
  readonly tabIndex?: number;
  readonly toggleClass: string;
  readonly titleClass?: string;
  readonly collapsibleClass?: string;
  readonly onToggleClick: () => void;
  readonly url?: string;
  readonly showPremiumIndicator: boolean;
}

const TeamAvatar = ({ avatarURL, icon }: AvatarProps) => {
  if (avatarURL) {
    return (
      <span
        className={styles.avatar}
        style={{ backgroundImage: `url(${avatarURL})` }}
      />
    );
  } else if (icon) {
    return <Icon small className={styles.icon} name={icon} />;
  }

  return null;
};

export const Collapsible: React.FunctionComponent<CollapsibleProps> = ({
  avatarURL,
  icon,
  title,
  open,
  onToggleClick,
  children,
  collapsibleClass,
  headerClass,
  tabIndex,
  titleClass,
  toggleClass,
  url,
  showPremiumIndicator,
}) => {
  const sendTeamAnalytics = () => {
    trackUe({
      category: Noun.BOARDS_MENU,
      verb: open ? Verb.CLOSES : Verb.OPENS,
      directObj: Noun.TEAM,
    });
  };

  const toggleCollapsibleSection = () => {
    trackUe({
      category: Noun.COLLAPSIBLE_SECTION,
      verb: open ? Verb.CLOSES : Verb.OPENS,
      directObj: Noun.BOARDS_MENU,
    });

    onToggleClick();
  };

  const titleContents = (
    <>
      <TeamAvatar avatarURL={avatarURL} icon={icon} />
      <span className={classNames(styles.title, titleClass)}>{title}</span>
    </>
  );

  const premiumIcon = !!showPremiumIndicator && (
    <Icon small className={styles.premiumIcon} name={IconName.Enterprise} />
  );

  return (
    <div className={classNames(styles.collapsible, collapsibleClass)}>
      <div className={classNames(styles.header, headerClass)}>
        {url ? (
          <RouterLink
            onClick={sendTeamAnalytics}
            href={url}
            className={styles.headerLink}
          >
            {titleContents}
            {premiumIcon}
          </RouterLink>
        ) : (
          <span className={classNames(styles.title, titleClass)}>
            {titleContents}
            {premiumIcon}
          </span>
        )}
        <button
          onClick={toggleCollapsibleSection}
          className={classNames(styles.toggle, toggleClass)}
          tabIndex={tabIndex}
        >
          {open ? (
            <Icon small quiet name={IconName.Remove} />
          ) : (
            <Icon small quiet name={IconName.Add} />
          )}
        </button>
      </div>
      <div className={open ? styles.open : styles.closed}>{children}</div>
    </div>
  );
};

export default Collapsible;
